// src/components/VisitorsDetails.jsx
import { useEffect, useRef } from "react";
import axios from "axios";
import { db, timestamp } from "../config/firebase";
import { addDoc, collection } from "firebase/firestore";

const TRACKED_KEY = "visitor_tracked";

const VisitorsDetails = ({ googleUser }) => {
  const dataSavedRef = useRef(false);

  const sanitizeValue = (value) => (value === undefined ? null : value);

  const fetchDataAndSave = async (latitude, longitude, ip, locationData) => {
    try {
      const userAgent = navigator.userAgent;
      const referrer = document.referrer || "Direct";

      // Create a Date object
      const now = new Date();

      // Format the date and time
      const formattedDateTime = now.toLocaleString("en-GB", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      const userDetails = {
        ip: sanitizeValue(ip),
        city: sanitizeValue(
          locationData?.address?.city || locationData?.city || null,
        ),
        region: sanitizeValue(
          locationData?.address?.state || locationData?.region || null,
        ),
        country_name: sanitizeValue(
          locationData?.address?.country || locationData?.country_name || null,
        ),
        postal: sanitizeValue(
          locationData?.address?.postcode || locationData?.postal || null,
        ),
        userAgent,
        referrer,
        timestamp,
        dateTime: formattedDateTime,
        latitude: typeof latitude === "number" ? latitude : null,
        longitude: typeof longitude === "number" ? longitude : null,
        // Google profile fields (present only when visitor signed in)
        ...(googleUser
          ? {
              authMethod: "google",
              googleName: googleUser.displayName || null,
              googleEmail: googleUser.email || null,
              googlePhotoUrl: googleUser.photoURL || null,
              googleUid: googleUser.uid || null,
            }
          : { authMethod: "anonymous" }),
      };
      await addDoc(collection(db, "Portfolio Visitors"), userDetails);
      dataSavedRef.current = true;
      localStorage.setItem(TRACKED_KEY, "true");
      await axios.post("/.netlify/functions/send-visitor-details", userDetails);
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  const getLocation = (ip) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => showPosition(position, ip),
        (error) => handleGeolocationError(error, ip),
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
      fetchLocationFromIP(ip);
    }
  };

  const showPosition = async (position, ip) => {
    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;

    try {
      const locationResponse = await axios.get(
        `https://geocode.maps.co/reverse?lat=${latitude}&lon=${longitude}&api_key=6675a9d07ccb8304599055kqj33bc90`,
      );
      const locationData = locationResponse.data;

      fetchDataAndSave(latitude, longitude, ip, locationData);
    } catch (error) {
      console.error("Error fetching location data from coordinates:", error);
      fetchLocationFromIP(ip);
    }
  };

  const handleGeolocationError = (error, ip) => {
    console.error("Error getting geolocation:", error);
    fetchLocationFromIP(ip);
  };

  const fetchLocationFromIP = async (ip) => {
    if (
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
    ) {
      fetchDataAndSave(null, null, ip, {});
      return;
    }

    try {
      // Fetch location data based on IP
      const locationResponse = await axios.get(`https://ipapi.co/${ip}/json/`);
      const locationData = locationResponse.data;

      fetchDataAndSave(null, null, ip, locationData);
    } catch (error) {
      console.error("Error fetching location data from IP:", error);
      fetchDataAndSave(null, null, ip, {});
    }
  };

  const fetchIPAndLocation = async () => {
    if (
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
    ) {
      fetchDataAndSave(null, null, null, {});
      return;
    }

    try {
      // Fetch IP address
      const ipResponse = await axios.get("https://api.ipify.org?format=json");
      const ip = ipResponse.data.ip;

      getLocation(ip);
    } catch (error) {
      console.error("Error fetching IP address:", error);
      fetchDataAndSave(null, null, null, {});
    }
  };

  useEffect(() => {
    // Wait until the visitor has made their auth decision (signed in or skipped)
    // googleUser === undefined means the LoginGate hasn't been dismissed yet
    if (googleUser === undefined) return;

    // Check if visitor has already been tracked (persists across sessions)
    const isTracked = localStorage.getItem(TRACKED_KEY);

    if (!dataSavedRef.current && !isTracked) {
      fetchIPAndLocation();
    }
    // eslint-disable-next-line
  }, [googleUser]);

  return "";
};

export default VisitorsDetails;
